'use client';

import React, { useEffect, useState } from 'react';
import { ArrowIcon, LinkText, useTheme } from '../../../../ui';
import styled from '@emotion/styled';
import { INewsAlert } from '../../api/contentful/types';
import { Dialog, DialogHeader, DialogMain } from '../dialog';
import { useTranslation } from '../../i18n/translate/useTranslation';
import { StyledDialogContent } from '../dialog/DialogContent';
import {
	getSessionStorageItem,
	setSessionStorageItem,
} from '../../utils/sessionStorage';
import Responsive from '../../../../ui/utils/Responsive';
import themed from '../../../../ui/themer/themed';

interface IAlertDialogProps {
	onClose: () => void;
	newsAlert: INewsAlert;
}

export const AlertDialog = ({ onClose, newsAlert }: IAlertDialogProps) => {
	const [isDialogClosed, setIsDialogClosed] = useState(true);
	const isMobileXL = Responsive.useMatchesBreakpoint('mobileXL');

	useEffect(() => {
		const dialogClosedInSection = !!getSessionStorageItem('news-dialog-closed');
		setIsDialogClosed(dialogClosedInSection);
	}, []);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const theme: any = useTheme();
	const IconComponent = theme.link.ArrowLinkIcon ?? ArrowIcon;
	const { t } = useTranslation();

	const url = newsAlert.link || undefined;
	const defaultLinkLabel = url
		? t('newsroom_emergency_information_read_more')
		: t('newsroom_emergency_information_dismiss');

	const onCloseDialog = () => {
		setSessionStorageItem('news-dialog-closed', true.toString());
		onClose();
	};

	if (isDialogClosed) return null;

	return (
		<StyledDialog
			onClose={onCloseDialog}
			ariaLabel={t('newsroom_emergency_information_title')}
			fullWidthMobile={false}
		>
			<StyledDialogHeader
				title={newsAlert.title}
				alignTitleAndCloseButton={!!isMobileXL}
			/>
			<StyledDialogMain>
				<StyledModalBody>{newsAlert.body}</StyledModalBody>
				<StyledReadMoreLink href={url} onClick={url ? undefined : onClose}>
					<LinkText>{newsAlert.linkLabel || defaultLinkLabel}</LinkText>
					{url && <IconComponent aria-hidden />}
				</StyledReadMoreLink>
			</StyledDialogMain>
		</StyledDialog>
	);
};

const StyledDialog = styled(Dialog)`
	${StyledDialogContent} {
		border-radius: 12px;
	}
`;

const StyledDialogMain = styled(DialogMain)`
	padding: 10px 0 40px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: flex;
		flex-direction: column;
	}
`;

const StyledDialogHeader = styled(DialogHeader)`
	font-size: 34px;
	font-weight: bold;
	line-height: 38px;

	h3 {
		max-width: 636px;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		h3 {
			max-width: calc(588px + (16px * 2));
		}
	}
`;

const StyledModalBody = styled.div`
	font-size: 18px;
	color: ${themed(({ color }) => color.text.default)};
	margin-bottom: 24px;
	line-height: 21px;
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: 16px;
	}
`;

const StyledReadMoreLink = styled.a`
	display: inline-flex;
	align-items: center;

	span {
		font-size: 18px;
		font-weight: 400;
		line-height: 21.6px;
	}
	&:hover {
		text-decoration: underline;
	}

	svg {
		margin-left: 12px;
		width: 16px;

		g {
			stroke: ${themed(({ color }) => color.text.hover)};
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		align-self: flex-end;
	}
`;
